import { Flex, useDisclosure } from '@chakra-ui/react';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Button, FormField, Text } from '@revelio/ui';

import { DataBuilderSettingsModal } from './data-builder-settings-modal';
import {
  countSubscribedDatasets,
  summarizeDataBuilderSettings,
} from './helpers/settings-summary';
import { useDataBuilderSettings } from './use-data-builder-settings';

/**
 * When present (`=true`) on the edit screen, the Data Builder Configuration
 * modal auto-opens once the settings have loaded. Lets callers (e.g. the
 * post-create client flow) deep-link straight into configuration.
 */
export const OPEN_DATA_BUILDER_CONFIG_PARAM = 'openDataBuilderConfig';

type EditFlowProps = {
  clientName: string | null | undefined;
  readonly?: boolean;
};

const EditFlow = ({ clientName, readonly }: EditFlowProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [searchParams, setSearchParams] = useSearchParams();
  const dbSettings = useDataBuilderSettings({ clientName });
  const { values, groups, isLoading } = dbSettings;

  const count = countSubscribedDatasets(values);
  const isConfigured = count !== null && count > 0;
  // Gate Edit on groups.length to avoid opening an empty modal before the
  // get-settings query has resolved.
  const notReady = isLoading || groups.length === 0;

  const shouldAutoOpen =
    searchParams.get(OPEN_DATA_BUILDER_CONFIG_PARAM) === 'true';

  useEffect(() => {
    if (!shouldAutoOpen || !clientName || isLoading) {
      return;
    }
    // The settings query has settled (loaded or errored). Consume the param
    // either way so it can't linger on the URL and auto-open the modal for a
    // different client later; only open when there is something to configure.
    if (!readonly && groups.length > 0) {
      onOpen();
    }
    const next = new URLSearchParams(searchParams);
    next.delete(OPEN_DATA_BUILDER_CONFIG_PARAM);
    setSearchParams(next, { replace: true });
  }, [
    shouldAutoOpen,
    clientName,
    isLoading,
    readonly,
    groups.length,
    onOpen,
    searchParams,
    setSearchParams,
  ]);

  return (
    <FormField label="Data Builder Configuration">
      <Flex alignItems="center" gap="2" flexWrap="wrap">
        <Text
          fontSize="sm"
          weight={isConfigured ? 'semibold' : 'regular'}
          data-testid="data-builder-settings-summary"
        >
          {summarizeDataBuilderSettings(values)}
        </Text>
        <Button
          variant="ghost"
          color="primary"
          size="sm"
          type="button"
          onClick={onOpen}
          disabled={readonly || notReady}
          style={{ height: 'auto', minHeight: 0, padding: '2px 6px' }}
          data-testid="data-builder-settings-edit"
        >
          Edit
        </Button>
        <DataBuilderSettingsModal
          isOpen={isOpen}
          onClose={onClose}
          dbSettings={dbSettings}
          clientName={clientName}
          readonly={readonly}
        />
      </Flex>
    </FormField>
  );
};

const AddFlow = () => (
  <FormField label="Data Builder Configuration">
    <Flex alignItems="center" gap="2" flexWrap="wrap">
      <Text fontSize="sm">Will be editable after client creation</Text>
      <Button
        variant="ghost"
        size="sm"
        type="button"
        disabled
        style={{ height: 'auto', minHeight: 0, padding: '2px 6px' }}
      >
        Edit
      </Button>
    </Flex>
  </FormField>
);

export const DataBuilderEditor = {
  AddFlow,
  EditFlow,
};
